import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft, FiCheck, FiMinus } from 'react-icons/fi';
import { useSubscription } from '../../context/SubscriptionContext';
import PaymentModal from './PaymentModal';

const PLANS = [
  {
    id: 'free',
    name: 'Glow Starter',
    price: 0,
    label: 'Free',
    description: 'Start your skin journey',
    accent: '#94a3b8',
    gradientFrom: '#f1f5f9',
    gradientTo: '#f8fafc',
    badge: null,
    features: [
      { text: '10 log entries / month', included: true },
      { text: '10 photo uploads total', included: true },
      { text: 'Basic skin tag tracking', included: true },
      { text: 'AM/PM routines (5 products max)', included: true },
      { text: 'Glow score tracking', included: true },
      { text: 'Timeline filters & sorting', included: false },
      { text: 'Glow analytics & charts', included: false },
      { text: 'Timelapse creation', included: false },
      { text: 'Wishlist', included: false },
      { text: 'Data export', included: false },
    ],
  },
  {
    id: 'glow',
    name: 'Glow',
    price: 14.99,
    label: '$14.99',
    monthlyEquiv: '~$1.25 / mo',
    description: 'Unlock your full glow potential',
    accent: '#5C8A78',
    gradientFrom: '#ecfdf5',
    gradientTo: '#f0fdfa',
    badge: 'Most Popular',
    features: [
      { text: 'Unlimited log entries', included: true },
      { text: '100 photo uploads', included: true },
      { text: 'Full timeline filters & sorting', included: true },
      { text: 'Glow analytics & trend charts', included: true },
      { text: 'Timelapse (30-day range)', included: true },
      { text: 'Wishlist (Products + Treatments)', included: true },
      { text: 'Data export (CSV)', included: true },
      { text: 'Unlimited routine products', included: true },
      { text: 'AI skin insights', included: false },
      { text: 'Priority support', included: false },
    ],
  },
  {
    id: 'radiant',
    name: 'Radiant Pro',
    price: 24.99,
    label: '$24.99',
    monthlyEquiv: '~$2.08 / mo',
    description: 'Maximum glow, zero compromise',
    accent: '#7c3aed',
    gradientFrom: '#faf5ff',
    gradientTo: '#f5f3ff',
    badge: 'Best Value',
    features: [
      { text: 'Everything in Glow', included: true },
      { text: 'Unlimited photos', included: true },
      { text: 'Unlimited timelapse (all time)', included: true },
      { text: 'AI skin insights & weekly reports', included: true },
      { text: 'Supplement effectiveness scoring', included: true },
      { text: 'Treatment progress history', included: true },
      { text: 'Data export (CSV + JSON)', included: true },
      { text: 'Priority support', included: true },
    ],
  },
];

function PlanCard({ plan, isCurrent, currentPlanPrice, onSelect, delay }) {
  const isUpgrade = plan.price > currentPlanPrice;
  const isDowngrade = plan.price < currentPlanPrice && plan.price > 0;

  let ctaLabel = `Get ${plan.name}`;
  if (plan.price === 0 && !isCurrent) ctaLabel = 'Downgrade to Free';
  else if (isUpgrade) ctaLabel = `Upgrade to ${plan.name}`;
  else if (isDowngrade) ctaLabel = `Switch to ${plan.name}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="rounded-3xl overflow-hidden relative"
      style={{
        border: `2px solid ${isCurrent ? plan.accent : '#e7e5e4'}`,
        background: `linear-gradient(150deg, ${plan.gradientFrom} 0%, ${plan.gradientTo} 100%)`,
      }}
    >
      {plan.badge && (
        <div className="absolute top-4 right-4 z-10">
          <span
            className="font-sans text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full text-white"
            style={{ background: plan.accent }}
          >
            {plan.badge}
          </span>
        </div>
      )}

      {isCurrent && (
        <div className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl" style={{ background: plan.accent }} />
      )}

      <div className="p-5">
        <h2 className="font-serif text-2xl text-stone-900">{plan.name}</h2>
        <p className="font-sans text-xs text-stone-500 mt-0.5 mb-3">{plan.description}</p>

        <div className="flex items-end gap-1.5 mb-1">
          <span className="font-serif text-4xl text-stone-900 leading-none">{plan.label}</span>
          {plan.price > 0 && <span className="font-sans text-sm text-stone-400 pb-1">/ year</span>}
        </div>
        {plan.monthlyEquiv && (
          <p className="font-sans text-xs mb-4" style={{ color: plan.accent }}>{plan.monthlyEquiv}</p>
        )}
        {plan.price === 0 && <div className="mb-4" />}

        <div className="space-y-2.5 mb-5">
          {plan.features.map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center"
                style={{ background: f.included ? `${plan.accent}20` : '#f1f5f9' }}
              >
                {f.included ? (
                  <SafeIcon icon={FiCheck} className="text-xs" style={{ color: plan.accent }} />
                ) : (
                  <SafeIcon icon={FiMinus} className="text-xs text-stone-300" />
                )}
              </div>
              <span className={`font-sans text-sm ${f.included ? 'text-stone-700' : 'text-stone-400'}`}>
                {f.text}
              </span>
            </div>
          ))}
        </div>

        {isCurrent ? (
          <div
            className="w-full py-3.5 rounded-full border-2 text-center font-sans font-semibold text-sm"
            style={{ borderColor: plan.accent, color: plan.accent }}
          >
            Current Plan
          </div>
        ) : plan.price === 0 ? (
          <button
            onClick={() => onSelect(plan)}
            className="w-full py-3.5 rounded-full border border-stone-300 text-stone-500 font-sans font-medium text-sm text-center active:bg-stone-50"
          >
            {ctaLabel}
          </button>
        ) : (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => onSelect(plan)}
            className="w-full py-3.5 rounded-full font-sans font-semibold text-sm text-white text-center shadow-md"
            style={{
              background: `linear-gradient(135deg, ${plan.accent}, ${plan.id === 'glow' ? '#7ecdc4' : '#a855f7'})`,
            }}
          >
            {ctaLabel}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

export default function PricingView({ onClose }) {
  const { planId, plan, upgradeTo, cancelSubscription } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const currentPlanPrice = PLANS.find((p) => p.id === planId)?.price ?? 0;

  const handleSelect = (plan) => {
    if (plan.price === 0) {
      setShowCancelConfirm(true);
    } else {
      setSelectedPlan(plan);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#f8fafc] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-20%] w-[70%] h-[50%] rounded-full bg-[#d1ece5] blur-3xl opacity-50" />
        <div className="absolute top-[40%] right-[-25%] w-[65%] h-[55%] rounded-full bg-[#e6e0f8] blur-3xl opacity-50" />
        <div className="absolute bottom-[-15%] left-[5%] w-[80%] h-[50%] rounded-full bg-[#ffe0d2] blur-3xl opacity-40" />
      </div>

      <div className="relative z-10 flex-shrink-0 px-5 pt-10 pb-4 flex items-center gap-3">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/80 border border-stone-200 flex items-center justify-center text-stone-600 active:bg-white shadow-sm flex-shrink-0"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg" />
        </button>
        <div>
          <h1 className="font-serif text-2xl text-stone-900 leading-tight">Choose Your Plan</h1>
          <p className="font-sans text-xs text-stone-400">Billed annually · Cancel anytime</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-10 relative z-10 space-y-4">
        {PLANS.map((p, i) => (
          <PlanCard
            key={p.id}
            plan={p}
            isCurrent={planId === p.id}
            currentPlanPrice={currentPlanPrice}
            onSelect={handleSelect}
            delay={i * 0.07}
          />
        ))}
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {selectedPlan && (
          <PaymentModal
            plan={selectedPlan}
            onClose={() => setSelectedPlan(null)}
          />
        )}
      </AnimatePresence>

      {/* Downgrade to Free Confirm */}
      <AnimatePresence>
        {showCancelConfirm && (
          <div
            className="absolute inset-0 z-50 flex items-end bg-black/30 backdrop-blur-sm"
            onClick={() => setShowCancelConfirm(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-white rounded-t-3xl px-6 pb-10 pt-6 shadow-2xl"
            >
              <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-6" />
              <h3 className="font-serif text-2xl text-stone-900 text-center mb-2">Downgrade to Free?</h3>
              <p className="font-sans text-sm text-stone-500 text-center mb-8 leading-relaxed">
                You'll lose access to photos, analytics, timelapse, and your wishlist at the end of your billing period.
              </p>
              <button
                onClick={() => { cancelSubscription(); setShowCancelConfirm(false); onClose(); }}
                className="w-full py-4 rounded-full font-sans font-semibold text-white bg-red-500 mb-3"
              >
                Yes, Downgrade
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="w-full py-4 rounded-full font-sans font-medium text-stone-500 bg-stone-50 border border-stone-200"
              >
                Keep My Plan
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
